import React, { FC, PropsWithChildren } from 'react';
import localFont from 'next/font/local';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-phone-input-2/lib/bootstrap.css';
import '@splidejs/splide/dist/css/splide.min.css';
import './styles/globals.css';
import MwIcons from '@/components/icons';
import dynamic from 'next/dynamic';
import ClientToastContainer from '@/components/client-toast-container';
import { darkModePaths, isProduction } from '@/utils/common-helpers';
import Chat from '@/components/chat/chat';
import { GoogleRecaptchaWrapper } from '@/hooks/google-recaptcha-wrapper';
import Script from 'next/script';
import Image from 'next/image';
import ClientLayoutWrapper from '@/components/client-layout-wrapper';
import FontLoader from '@/components/font-loader';
import { headers } from 'next/headers';
import { Chatbot } from '@/components/chatbot';

const proximaNova = localFont({
    src: [
        {
            path: '/fonts/proxima-nova-regular.woff2',
            weight: '400',
            style: 'normal',
        },
        {
            path: '/fonts/proxima-nova-medium.woff2',
            weight: '500',
            style: 'normal',
        },
        {
            path: '/fonts/proxima-nova-semibold.woff2',
            weight: '600',
            style: 'normal',
        },
        {
            path: '/fonts/proxima-nova-bold.woff2',
            weight: '700',
            style: 'normal',
        },
        {
            path: '/fonts/proximanovacond_bold.woff2',
            weight: '800',
            style: 'normal',
        },
    ],
    display: 'swap',
    variable: '--mw-font-family',
});
const proximanovacond = localFont({
    src: [
        {
            path: '/fonts/proximanovacond_bold.woff2',
            weight: '700',
            style: 'normal',
        },
    ],
    display: 'swap',
    variable: '--mw-font-family-condensed',
});
import Header from '@/components/header';
import Footer from '@/components/footer';

const FlowbiteContext = dynamic(() => import('./context/FlowbiteContext'), {
    loading: () => (
        <div id="preloader">
            <div className="lds-ripple">
                <div></div> <div></div>
            </div>
        </div>
    ),
});

const RootLayout: FC<PropsWithChildren> = async function ({ children }) {
    // Read nonce from middleware request header
    const headersList = await headers();
    const nonce = headersList.get('x-nonce') || '';

    return (
        <html lang="en" suppressHydrationWarning>
            <head>
                {!isProduction() && <meta name="robots" content={'noindex,nofollow'} />}

                {/* Set dark mode paths as global variable */}
                <script
                    nonce={nonce}
                    dangerouslySetInnerHTML={{
                        __html: `window.__DARK_MODE_PATHS__ = ${JSON.stringify(darkModePaths)};`,
                    }}
                />
                <Script
                    id="apollo-tracker"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                        __html: `
      function initApollo(){
        var n=Math.random().toString(36).substring(7),
        o=document.createElement("script");
        o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n;
        o.async=true;
        o.defer=true;
        o.onload=function(){
          window.trackingFunctions.onLoad({
            appId: "69bd22281bb92a000d8c0990"
          });
        };
        document.head.appendChild(o);
      }
      initApollo();
    `,
                    }}
                />
                {/* Prevent dark mode flash by setting class before render */}
                <script
                    nonce={nonce}
                    dangerouslySetInnerHTML={{
                        __html: `
                            (function() {
                                var paths = window.__DARK_MODE_PATHS__ || [];
                                var currentPath = window.location.pathname;
                                if (paths.indexOf(currentPath) !== -1) {
                                    document.documentElement.classList.add('dark');
                                } else {
                                    document.documentElement.classList.remove('dark');
                                }
                            })();
                        `,
                    }}
                />

                {isProduction() && (
                    <>
                        <Script
                            id="gtag"
                            strategy="afterInteractive"
                            src="https://www.googletagmanager.com/gtag/js?id=G-4Q84PX3MPE"
                            crossOrigin="anonymous"
                            nonce={nonce}
                        />
                        <Script
                            id="google-analytics"
                            strategy="afterInteractive"
                            nonce={nonce}
                            dangerouslySetInnerHTML={{
                                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', 'G-4Q84PX3MPE');
                `,
                            }}
                        />
                        <Script
                            id="facebook-pixel"
                            strategy="afterInteractive"
                            nonce={nonce}
                            dangerouslySetInnerHTML={{
                                __html: `
                        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                          !(function(f,b,e,v,n,t,s) {
                            if(f.fbq) return;
                            n=f.fbq=function() {
                              n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                            };
                            if(!f._fbq) f._fbq=n;
                            n.push=n;
                            n.loaded=!0;
                            n.version='2.0';
                            n.queue=[];
                            t=b.createElement(e);
                            t.async=!0;
                            t.src=v;
                            t.crossOrigin='anonymous';
                            t.onerror=function(){};
                            s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t, s);
                          })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
                          try {
                            fbq('init', '8772218662821727');
                            fbq('track', 'PageView');
                          } catch(e) {}
                        } else {
                          window.fbq = function() {};
                        }
                      `,
                            }}
                        />

                        {/* Fallback noscript pixel for users with JavaScript disabled */}
                        <noscript>
                            <Image
                                height="1"
                                width="1"
                                style={{ display: 'none' }}
                                src="https://www.facebook.com/tr?id=8772218662821727&ev=PageView&noscript=1"
                                alt="facebook-pixel"
                                loading="lazy"
                            />
                        </noscript>
                    </>
                )}

                {/* clarity script given by jm sir  */}
                <Script
                    id="clarity-script"
                    strategy="afterInteractive"
                    nonce={nonce}
                    dangerouslySetInnerHTML={{
                        __html: `
                            (function(c,l,a,r,i,t,y){
                                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                                t.crossOrigin='anonymous';
                                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                            })(window, document, "clarity", "script", "mcchesx7ez");
                        `,
                    }}
                />
                <script
                    type="application/ld+json"
                    nonce={nonce}
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'Moweb Technologies Pvt. Ltd',
                            url: 'https://www.moweb.com/',
                            logo: 'https://www.moweb.com/img/logo-moweb.svg',
                            contactPoint: {
                                '@type': 'ContactPoint',
                                telephone: '+91-9712992717',
                                contactType: 'customer support',
                            },
                            sameAs: [
                                'https://www.facebook.com/mowebtech',
                                'https://x.com/mowebtech',
                                'https://www.instagram.com/mowebtechnologies/',
                                'https://www.linkedin.com/company/moweb-technologies-pvt-ltd/posts/?feedView=all',
                            ],
                        }),
                    }}
                />
            </head>
            <FlowbiteContext>
                <body className="font-sans text-textcolor bg-white dark:bg-black dark:text-light">
                    <GoogleRecaptchaWrapper
                        reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
                        className="z-50"
                        scriptProps={{
                            async: false,
                            defer: true,
                            appendTo: 'head',
                            nonce: nonce,
                        }}
                        language="en" // Replace with your desired language if needed
                    >
                        <FontLoader
                            proximaNovaVariable={proximaNova.variable}
                            proximaNovaCondVariable={proximanovacond.variable}
                        />
                        <ClientLayoutWrapper>
                            <Header />
                            <Chat />
                            {children}
                            <Footer />
                            <Chatbot />
                            <MwIcons />
                            <ClientToastContainer />
                        </ClientLayoutWrapper>
                    </GoogleRecaptchaWrapper>
                </body>
            </FlowbiteContext>
        </html>
    );
};

export default RootLayout;
